import { useAccountsService } from '@restworld/data-services';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { atom, useAtom, useSetAtom } from 'jotai';
import { setIsEnterPhoneNumber, setIsValidateOtp } from '../components/phone-number/states';
import { useTranslation } from 'next-i18next';
import { useAnalytics } from '@restworld/utils-common';

export interface PhoneNumberInput {
  phoneNumber: string;
}

export type UseVerifyPhoneNumberProps = {
  phoneNumber?: string;
};
export const smsSentCountProfileAtom = atom(0);
export const phoneNumberAtom = atom<string>('');

export function useVerifyPhoneNumberForm({ phoneNumber }: UseVerifyPhoneNumberProps) {
  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors },
    setValue,
    setError,
    getValues,
  } = useForm<PhoneNumberInput>({
    defaultValues: {
      phoneNumber,
    },
  });
  const [, setSmsSentCount] = useAtom(smsSentCountProfileAtom);
  const [loading, setLoading] = useState<boolean>(false);
  const setEnterPhoneNumberContent = useSetAtom(setIsEnterPhoneNumber);
  const setValidateOtpContent = useSetAtom(setIsValidateOtp);
  const { t } = useTranslation('user-settings');
  const [, setPhoneNumber] = useAtom(phoneNumberAtom);
  const acc = useAccountsService();
  const { trackEvent } = useAnalytics();

  const updatePhoneNumber = useCallback(
    async (data: PhoneNumberInput) => {
      setLoading(true);
      try {
        setPhoneNumber(data.phoneNumber);
        await acc.updateAuthenticationDetails({ phoneNumber: data.phoneNumber });
        trackEvent('worker_request_otp_for_phone_number_change', { phone_number: data.phoneNumber });
        setSmsSentCount((prevSMSCount) => prevSMSCount + 1);
        setLoading(false);
        setEnterPhoneNumberContent(false);
        setValidateOtpContent(true);
      } catch (error) {
        setLoading(false);
        if (error?.['code'] === 409)
          setError('phoneNumber', {
            type: 'manual',
            message: t('enter_phone_number_submitted_phone_number_already_exists'),
          });
        else
          setError('phoneNumber', {
            type: 'manual',
            message: t('enter_phone_number_submitted_phone_number_invalid'),
          });
      }
    },
    [acc, setEnterPhoneNumberContent, setError, setPhoneNumber, setSmsSentCount, setValidateOtpContent, t, trackEvent]
  );

  const requestNewOTPCode = useCallback(async () => {
    setLoading(true);
    try {
      await acc.sendVerificationPhoneOtp();
      trackEvent('worker_request_otp_for_phone_number_change', { resend_code: true });
      setSmsSentCount((prevSMSCount) => prevSMSCount + 1);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }, [acc, setSmsSentCount, trackEvent]);

  const onSubmit = useCallback(
    async (data: PhoneNumberInput) => {
      updatePhoneNumber(data);
    },
    [updatePhoneNumber]
  );

  return {
    loading,
    onSubmit,
    register,
    handleSubmit,
    errors,
    watch,
    control,
    setValue,
    setError,
    setLoading,
    requestNewOTPCode,
    getValues,
  };
}
